<template>
  <RevContainer v-if="!isWebview && breads.length > 0">
    <RevBreadcrumbs
      :ariaLabel="i18n(translations.breadcrumbAriaLabel)"
      :breads
    />
  </RevContainer>
</template>

<script setup lang="ts">
import { useRoute } from '#imports'
import { computed } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useIsWebview } from '@backmarket/nuxt-module-navigation/useIsWebview'
import { RevBreadcrumbs } from '@ds/components/Breadcrumbs'
import type { Bread } from '@ds/components/Breadcrumbs/Breadcrumbs.vue'
import { RevContainer } from '@ds/components/Container'

import { useRouteLocationWithLocale } from '~/composables/useRouteLocationWithLocale'
import { HOME } from '~/scopes/home/route-names'

import { OUTLET_LANDING_CARDS } from '../../constants'
import { OUTLET } from '../../routes-names'

import translations from './OutletBreadcrumb.translations'

const props = defineProps<{
  categorySlug?: string
  productTitle?: string
  categoryId?: number
}>()

const i18n = useI18n()
const route = useRoute()
const isWebview = useIsWebview()
const resolveLocalizedRoute = useRouteLocationWithLocale()

const breads = computed<Bread[]>(() => {
  const breadcrumbs: Bread[] = []

  // Always add Home as first item
  breadcrumbs.push({
    title: i18n(translations.home),
    link: resolveLocalizedRoute({ name: HOME }),
    tracking: {
      zone: 'outlet',
      value: 'breadcrumb',
      name: 'home',
      position: 'breadcrumb_1',
    },
  })

  // Always add Outlet Home as second item
  breadcrumbs.push({
    title: i18n(translations.outletHome),
    link: resolveLocalizedRoute({ name: OUTLET.HOME }),
    tracking: {
      zone: 'outlet',
      value: 'breadcrumb',
      name: 'outlet-home',
      position: 'breadcrumb_2',
    },
  })

  // Add category if provided or if we can determine it from categoryId
  const categorySlug =
    props.categorySlug ||
    (props.categoryId
      ? OUTLET_LANDING_CARDS.find((card) =>
          card.filter?.includes(`cat_id:"${props.categoryId}"`),
        )?.slug
      : undefined)

  if (categorySlug) {
    const category = OUTLET_LANDING_CARDS.find((c) => c.slug === categorySlug)
    if (category) {
      breadcrumbs.push({
        title: i18n(category.label),
        link: resolveLocalizedRoute({
          name: OUTLET.CATEGORY,
          params: { slug: categorySlug },
        }),
        tracking: {
          zone: 'outlet',
          value: 'breadcrumb',
          name: categorySlug,
          position: `breadcrumb_${breadcrumbs.length + 1}`,
        },
      })
    }
  }

  // Add product title if on product page (last item, not clickable)
  if (props.productTitle && route.name === OUTLET.DETAIL) {
    breadcrumbs.push({
      title: props.productTitle,
      link: route.fullPath,
      tracking: {
        zone: 'outlet',
        value: 'breadcrumb',
        name: 'product',
        position: `breadcrumb_${breadcrumbs.length + 1}`,
      },
    })
  }

  return breadcrumbs
})
</script>
